<script setup lang="ts">
interface Props {
  errors: number
  placeholder?: string
  modelValue?: string
  id: string
  name: string
  onBlur: (e: Event) => void
  onInput: (e: Event | unknown) => void
  onChange: (e: Event | unknown) => void
}

const props = defineProps<Props>()
const emit = defineEmits(['update:modelValue'])

const localValue = ref(props.modelValue)

watch(
  () => props.modelValue,
  (newValue) => {
    localValue.value = newValue
  }
)

const handleInput = (newValue: string | number) => {
  // Remove all non-digit characters
  const cleanValue = String(newValue).replace(/\D/g, '')

  // Limit to 9 digits
  const limitedValue = cleanValue.slice(0, 9)

  localValue.value = limitedValue

  if (props.errors === 0) {
    // Emit only on blur if no errors
    return
  }

  // If errors already exist - emit immediately
  emit('update:modelValue', limitedValue)
}

const handleBlur = (event: Event) => {
  if (props.errors === 0) {
    emit('update:modelValue', localValue.value)
  }
  props.onBlur(event)
}

const handleKeydown = (event: KeyboardEvent) => {
  const currentValue = localValue.value || ''
  const numericValue = currentValue.replace(/\D/g, '')
  const input = event.target as HTMLInputElement
  const hasSelection = input.selectionStart !== input.selectionEnd

  // Allow control keys (backspace, delete, tab, escape, enter, arrow keys, etc.)
  const allowedKeys = [
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'Enter',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
    'PageUp',
    'PageDown'
  ]

  // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
  if (event.ctrlKey || event.metaKey) {
    return
  }

  // Allow control keys
  if (allowedKeys.includes(event.key)) {
    return
  }

  // Only allow digits (0-9)
  if (!/\d/.test(event.key)) {
    event.preventDefault()
    return
  }

  // Prevent input if already 9 digits and no selection
  if (numericValue.length >= 9 && !hasSelection) {
    event.preventDefault()
  }
}

const handlePaste = (event: ClipboardEvent) => {
  // Allow default paste, then clean the value in next tick
  setTimeout(() => {
    const input = event.target as HTMLInputElement
    const value = input.value

    // Get cleaned value (digits only, remove leading zeros, max 9)
    const digitsOnly = value.replace(/\D/g, '').replace(/^0+/, '').slice(0, 9)

    if (value !== digitsOnly) {
      localValue.value = digitsOnly

      // Update input value directly to avoid cursor jumping
      input.value = digitsOnly

      if (props.errors > 0) {
        emit('update:modelValue', digitsOnly)
      }
    }
  }, 0)
}
</script>

<template>
  <div
    :class="[
      'flex h-[2.25rem] items-center gap-0 overflow-hidden rounded-md border pl-2 ring-offset-2',
      errors
        ? 'border-red-200 bg-red-50 focus-within:ring-2 focus-within:ring-red-500'
        : 'border-gray-300 focus-within:ring-2 focus-within:ring-blue-600'
    ]"
  >
    <figure>
      <UiIcon name="kyrgyzcha" />
    </figure>
    <div class="px-1 text-sm">+996</div>
    <Input
      v-bind="$attrs"
      :id="`${props.id}-phone-input`"
      :model-value="localValue"
      type="tel"
      :placeholder="placeholder ?? '(___) ___-___'"
      :name="name"
      class="w-full rounded-none border-0 bg-transparent px-0 focus:ring-0 focus-visible:ring-0"
      @update:model-value="handleInput"
      @keydown="handleKeydown"
      @paste="handlePaste"
      @blur="handleBlur"
    />
  </div>
</template>
